import React from 'react';
import { PropTypes } from 'prop-types';
import { Field } from 'react-final-form';
import { InputField, NoteDisclosure, SelectField } from '@unite-us/app-components';
import { TextField } from '@unite-us/ui';
import { useEditReasonOptions } from '../../../hooks/useEditReasonOptions';
import DefaultAuthorizationFormFields from '../shared/DefaultAuthorizationFormFields';
import { ClinicalModificationsDropdown } from './ClinicalModificationsDropdown';

function AuthorizationDetailsSection({
  dateType,
  error,
  isUnitBased,
  isCostBased,
  feeScheduleProgram,
  initialClinicalModificationCodeValue,
  showEditReason,
}) {
  const editReasonOptions = useEditReasonOptions(feeScheduleProgram?.fee_schedule?.id, showEditReason);
  return (
    <>
      <hr className="my-5 border-dark-fill-blue border-solid" />
      <h4 className="uppercase leading-4 mb-3">
        Authorization Details
      </h4>
      <div className="grid grid-cols-2 gap-2 m-0 gap-x-24">
        <DefaultAuthorizationFormFields
          dateType={dateType}
          error={error}
          isUnitBased={isUnitBased}
          isCostBased={isCostBased}
          feeScheduleProgram={feeScheduleProgram}
          amountLabel="AUTHORIZED AMOUNT"
        />

        <Field name="payer_authorization_number">
          {(fieldProps) => (
            <InputField
              {...fieldProps}
              className="mb-0"
              id="payer_authorization_number"
              label="PAYER AUTHORIZATION ID"
            />
          )}
        </Field>

        {showEditReason && (
          <div className="mb-0">
            <label
              htmlFor="service_authorization_edit_reason"
              className="text-xs normal-case tracking-normal"
            >
              REASON FOR EDITING AUTHORIZATION
            </label>
            <Field name="service_authorization_edit_reason">
              {({ input, meta }) => (
                <SelectField
                  className="w-full mb-0 ml-0"
                  clearable
                  error={meta.touched && meta.error}
                  hideLabel
                  id="service_authorization_edit_reason"
                  invalid={meta.touched && !!meta.error}
                  label="REASON FOR EDITING AUTHORIZATION"
                  options={editReasonOptions}
                  placeholder="Select reason"
                  position="bottom"
                  {...input}
                />
              )}
            </Field>
          </div>
        )}

        {
          feeScheduleProgram.cm_code_required_on_authorization && (
            <ClinicalModificationsDropdown
              initialValue={initialClinicalModificationCodeValue}
            />
          )
        }

        <Field name="reviewer_note">
          {(props) => (
            <TextField
              {...props}
              className="!p-0 col-span-2"
              id="reviewer_note"
              label="NOTE"
              afterLabelContent={<NoteDisclosure />}
            />
          )}
        </Field>
      </div>
    </>
  );
}

AuthorizationDetailsSection.propTypes = {
  dateType: PropTypes.string.isRequired,
  error: PropTypes.string,
  isUnitBased: PropTypes.bool.isRequired,
  isCostBased: PropTypes.bool.isRequired,
  feeScheduleProgram: PropTypes.shape({
    unit: PropTypes.string,
    cm_code_required_on_authorization: PropTypes.bool,
    fee_schedule: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  initialClinicalModificationCodeValue: PropTypes.object,
  showEditReason: PropTypes.bool,
};

AuthorizationDetailsSection.defaultProps = {
  error: undefined,
  initialClinicalModificationCodeValue: null,
  showEditReason: false,
};

export default AuthorizationDetailsSection;
